import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  AnimatePresence,
} from "framer-motion";
import { Gem, Gift, TrendingUp, MoonStar, Play } from "lucide-react";

const ICONS = {
  wedding: Gem,
  gift: Gift,
  umkm: TrendingUp,
  masjid: MoonStar,
  influencer: Play,
};
const COLORS = {
  wedding: "#E8B4A8",
  gift: "#FF9A76",
  umkm: "#2AA79B",
  masjid: "#50C878",
  influencer: "#B06BFF",
};

export default function CustomCursor() {
  const [enabled] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 220, damping: 22 });
  const ry = useSpring(y, { stiffness: 220, damping: 22 });
  const [icon, setIcon] = useState(null);
  const [active, setActive] = useState(false);
  const [sparks, setSparks] = useState([]);
  const lastSpark = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.add("has-custom-cursor");

    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      const now = performance.now();
      if (now - lastSpark.current > 110) {
        lastSpark.current = now;
        setSparks((s) => [
          ...s.slice(-7),
          { id: now, x: e.clientX, y: e.clientY },
        ]);
      }
      const mag = e.target.closest?.("[data-magnetic]");
      if (mag) {
        const r = mag.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        mag.style.transform = `translate(${dx * 0.18}px, ${dy * 0.18}px)`;
      }
    };
    const over = (e) => {
      const ic = e.target.closest?.("[data-cursor-icon]");
      setIcon(ic ? ic.dataset.cursorIcon : null);
      setActive(!!e.target.closest?.("a,button,[data-magnetic]"));
    };
    const out = (e) => {
      const mag = e.target.closest?.("[data-magnetic]");
      if (mag) mag.style.transform = "";
    };

    window.addEventListener("mousemove", move, { passive: true });
    document.addEventListener("mouseover", over, { passive: true });
    document.addEventListener("mouseout", out, { passive: true });
    return () => {
      document.documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      document.removeEventListener("mouseout", out);
    };
  }, [enabled, x, y]);

  if (!enabled) return null;

  const Icon = icon ? ICONS[icon] : null;
  const color = icon ? COLORS[icon] : "#FFE9C4";

  return (
    <>
      <motion.div aria-hidden className="cursor-glow" style={{ x: rx, y: ry }} />
      <motion.div
        aria-hidden
        className="cursor-ring"
        style={{ x: rx, y: ry }}
        animate={{ scale: active || icon ? 1.45 : 1, borderColor: color }}
        transition={{ duration: 0.2 }}
        data-testid="custom-cursor-ring"
      >
        {Icon && <Icon size={15} color={color} />}
      </motion.div>
      <motion.div
        aria-hidden
        className="cursor-dot"
        style={{ x, y, backgroundColor: color }}
      />
      <AnimatePresence>
        {sparks.map((s) => (
          <motion.span
            key={s.id}
            aria-hidden
            className="cursor-spark"
            style={{ x: s.x, y: s.y }}
            initial={{ opacity: 0.8, scale: 1 }}
            animate={{ opacity: 0, scale: 0.15 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            onAnimationComplete={() =>
              setSparks((sp) => sp.filter((p) => p.id !== s.id))
            }
          />
        ))}
      </AnimatePresence>
    </>
  );
}
